import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import DimensionsSelector from './config/DimensionsSelector';
import FixationsSelector from './config/FixationsSelector';
import LambrequinSelector from './config/LambrequinSelector';
import FixationLambrequinSelector from './config/FixationLambrequinSelector';
import OptionsSupplementaires from './config/OptionsSupplementaires';
import FabricCalculationDisplay from './config/FabricCalculationDisplay';
import ToileAuMetreForm from './config/ToileAuMetreForm';
import ConfigurationPreview from './config/ConfigurationPreview';
import { calculateAvanceeAjustee } from './config/utils/calculationUtils';

const ConfigurationForm = ({ 
  basePrice, 
  onConfigChange, 
  productId, 
  isDoublePente = false, 
  isLambrequinSeul = false, 
  isPergola = false, 
  isBriseVue = false, 
  isToileAuMetre = false, 
  productWidth = null,
  // Props pour le marquage
  marquageText = '',
  marquageCalculation = null,
  marquageColor = 'white',
  // Props pour l'apparence du produit
  productImage = null,
  productRal = null,
  // Option pour cacher l'aperçu (si affiché ailleurs)
  hidePreview = false
}) => {
  // Regroupement des états des dimensions
  const [dimensions, setDimensions] = useState({
    largeur: 400,
    projection: isBriseVue ? 100 : 300,
    projection2: 300,
    metrage: 1,
    hauteurLambrequin: 25
  });

  // Regroupement des états des fixations
  const [fixations, setFixations] = useState({
    fixationHaute: 'jonc',
    fixationBasse: 'jonc',
    joncMedianPosition: 'sur',
    fixationLambrequin: 'jonc'
  });

  // Regroupement des états des diamètres
  const [diametres, setDiametres] = useState({
    joncHaute: 5.5,
    joncBasse: 5.5,
    fourreauBasse: 40,
    joncLambrequin: 5.5
  });

  // État du lambrequin
  const [lambrequin, setLambrequin] = useState(isLambrequinSeul ? 'droit' : 'aucun');

  // Regroupement des options supplémentaires
  const [sanglons, setSanglons] = useState({
    nombre: 0,
    unitPrice: 49
  });
  
  const [optionsSupp, setOptionsSupp] = useState({
    renfortLyre: false,
    textePersonnalise: false,
    agrafes: false,
    clips: false,
    oeillets: false,
    sanglons: false
  });
  
  const [renfortLyreDetails, setRenfortLyreDetails] = useState({
    nombre: 1,
    positions: [0]
  });
  
  const [typeAssemblage, setTypeAssemblage] = useState('haute-tenacite');
  
  const [lestageDetails, setLestageDetails] = useState({
    diametreTube: 14,
    nombreLests: 0
  });

  // États du calcul
  const [quantity, setQuantity] = useState(1);
  const [fabricCalculation, setFabricCalculation] = useState(null);
  const [calculationError, setCalculationError] = useState(null);
  const [detailCalculAvancee, setDetailCalculAvancee] = useState(null);
  const [showDetailCalcul, setShowDetailCalcul] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const largeurLaize = useMemo(() => 
    productWidth ? parseFloat(productWidth.replace(/[^\d.]/g, '')) || 120 : 120,
    [productWidth]
  );
  const isStoreDroit = useMemo(() => 
    !isDoublePente && !isLambrequinSeul && !isPergola && !isBriseVue && !isToileAuMetre,
    [isDoublePente, isLambrequinSeul, isPergola, isBriseVue, isToileAuMetre]
  );

  // Configuration des fixations selon le type de produit (memoized)
  const fixationsHaute = useMemo(() => {
    const joncOption = { id: 'jonc', label: 'Jonc', image: 'https://customer-assets.emergentagent.com/job_osyla-store-copy/artifacts/i49g727s_jonc.png' };
    if (isStoreDroit) {
      return [
        joncOption,
        { id: 'ourlet', label: 'Ourlet', image: 'https://customer-assets.emergentagent.com/job_ecommerce-store-28/artifacts/u2jt0yl1_finition_ourlet.png' },
        { id: 'finition-brute', label: 'Finition brute', image: 'https://customer-assets.emergentagent.com/job_ecommerce-store-28/artifacts/0iatgjjn_finition_brute.png' }
      ];
    }
    return [joncOption];
  }, [isStoreDroit]);

  const fixationsBasse = useMemo(() => {
    const joncOption = { id: 'jonc', label: 'Jonc', image: 'https://customer-assets.emergentagent.com/job_osyla-store-copy/artifacts/i49g727s_jonc.png' };
    const fourreauOption = { id: 'fourreau', label: 'Fourreau', image: 'https://customer-assets.emergentagent.com/job_osyla-store-copy/artifacts/wp8v5ipb_fourreau.png' };
    return [joncOption, fourreauOption];
  }, []);

  const fixationsLambrequin = useMemo(() => [
    ...fixationsBasse,
    { id: 'agrafe', label: 'Agrafé', image: 'https://customer-assets.emergentagent.com/job_storefixations/artifacts/k5vyhpu9_agrafe.png' },
    { id: 'ourlet', label: 'Ourlet (lambrequin agrafé)', image: 'https://customer-assets.emergentagent.com/job_ecommerce-store-28/artifacts/u2jt0yl1_finition_ourlet.png' },
    { id: 'fourreau-pince-interieur', label: 'Fourreau pincé intérieur', image: 'https://customer-assets.emergentagent.com/job_ecommerce-store-28/artifacts/glb1w979_fourreau_pince_interieur.png' },
    { id: 'fourreau-pince-exterieur', label: 'Fourreau pincé extérieur', image: 'https://customer-assets.emergentagent.com/job_ecommerce-store-28/artifacts/f3le5e0j_fourreau_pince_exterieur.png' },
    { id: 'fourreau-rapporte', label: 'Fourreau rapporté', image: 'https://customer-assets.emergentagent.com/job_storefixations/artifacts/k15aa3nf_fourreau_rapporte.png' }
  ], [fixationsBasse]);

  const allLambrequinOptions = useMemo(() => [
    { id: 'aucun', label: 'Pas de lambrequin', image: null },
    { id: 'droit', label: 'Coupe droite', image: 'https://customer-assets.emergentagent.com/job_osyla-store-copy/artifacts/4j7yhvhi_coupe-droite.png' },
    { id: 'ondule', label: 'Vague 1', image: 'https://customer-assets.emergentagent.com/job_osyla-store-copy/artifacts/879byfgf_coupe-ondulee.png' },
    { id: 'vagues', label: 'Vague 2', image: 'https://customer-assets.emergentagent.com/job_osyla-store-copy/artifacts/p5ia7isw_coupe-a-vagues.png' }
  ], []);
  
  const lambrequinOptions = useMemo(() => 
    isLambrequinSeul 
      ? allLambrequinOptions.filter(opt => opt.id !== 'aucun')
      : allLambrequinOptions,
    [isLambrequinSeul, allLambrequinOptions]
  );

  const optionsSupplementaires = useMemo(() => {
    if (isPergola) {
      return [
        { id: 'sans', label: 'Sans option' },
        { id: 'clips', label: 'Type d\'assemblage', tooltip: 'Clips pour fixation' },
        { id: 'sanglons', label: 'Sanglons', tooltip: 'Sangles de fixation espacées tous les mètres' }
      ];
    }
    if (isBriseVue) {
      return [
        { id: 'sans', label: 'Sans option' },
        { id: 'clips', label: 'Type d\'assemblage', tooltip: 'Clips pour fixation' }
      ];
    }
    return [
      { id: 'sans', label: 'Sans option' },
      { id: 'clips', label: 'Type d\'assemblage', tooltip: 'Clips pour fixation' },
      { id: 'oeillets', label: 'Lestage du lambrequin', tooltip: 'Pour fixation du lambrequin' }
    ];
  }, [isPergola, isBriseVue]);

  // Récupérer le prix unitaire des sanglons
  useEffect(() => {
    const fetchSanglonPrice = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/options-pricing/`);
        if (response.data && response.data.sanglon_price) {
          setSanglons(prev => ({ ...prev, unitPrice: response.data.sanglon_price }));
        }
      } catch (error) {
        console.error('Erreur récupération prix sanglon:', error);
      }
    };
    fetchSanglonPrice();
  }, [BACKEND_URL]);

  // Calculer automatiquement les positions des renforts (useCallback)
  const calculateRenfortPositions = useCallback((nombre, largeurToile) => {
    if (nombre === 1) return [largeurToile / 2];
    if (nombre === 2) return [largeurToile / 3, (2 * largeurToile) / 3];
    if (nombre === 3) return [largeurToile / 4, largeurToile / 2, (3 * largeurToile) / 4];
    return [largeurToile / 2];
  }, []);

  // Gestion des options supplémentaires (useCallback)
  const handleOptionChange = useCallback((optionId) => {
    setOptionsSupp(prev => {
      const newOptions = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      
      if (optionId !== 'sans') {
        newOptions[optionId] = true;
        
        if (optionId === 'sanglons' && sanglons.nombre === 0 && isPergola && fabricCalculation) {
          const largeurTotale = fabricCalculation.largeur_totale || dimensions.largeur + 12;
          const avanceeTotale = fabricCalculation.avancee_totale || dimensions.projection + 12;
          const nombreLargeur = Math.max(0, Math.floor(largeurTotale / 100) - 1);
          const nombreAvancee = Math.max(0, Math.floor(avanceeTotale / 100) - 1);
          const nombreDefaut = nombreLargeur * nombreAvancee;
          setSanglons(prev => ({ ...prev, nombre: nombreDefaut }));
        }
      } else {
        if (prev.sanglons) {
          setSanglons(prev => ({ ...prev, nombre: 0 }));
        }
      }
      
      return newOptions;
    });
  }, [sanglons.nombre, isPergola, fabricCalculation, dimensions.largeur, dimensions.projection]);

  // Mettre à jour les positions des renforts
  useEffect(() => {
    if (optionsSupp.renfortLyre) {
      const nouvelles_positions = calculateRenfortPositions(renfortLyreDetails.nombre, dimensions.largeur);
      setRenfortLyreDetails(prev => ({
        ...prev,
        positions: nouvelles_positions
      }));
    }
  }, [renfortLyreDetails.nombre, dimensions.largeur, optionsSupp.renfortLyre, calculateRenfortPositions]);

  // Calculer automatiquement le nombre de lests
  useEffect(() => {
    if (optionsSupp.oeillets) {
      const nombreCalcule = Math.ceil(dimensions.largeur / 100);
      setLestageDetails(prev => ({
        ...prev,
        nombreLests: nombreCalcule
      }));
    } else {
      setLestageDetails(prev => ({
        ...prev,
        nombreLests: 0
      }));
    }
  }, [dimensions.largeur, optionsSupp.oeillets]);

  // Calculer le métrage de tissu via l'API (useCallback)
  const calculateFabric = useCallback(async () => {
    if (!productId) return;
    
    try {
      setCalculationError(null);
      
      if (isToileAuMetre) {
        const response = await axios.post(`${BACKEND_URL}/api/calculate/fabric/`, {
          largeur: largeurLaize,
          avancee: dimensions.metrage * 100,
          hauteur_lambrequin: 0,
          product_id: String(productId),
          is_pergola: false,
          type_assemblage: typeAssemblage
        });
        setFabricCalculation(response.data);
        return;
      }
      
      let avanceeBase;
      let avancee2Value = null;
      
      if (isLambrequinSeul) {
        avanceeBase = parseFloat(dimensions.hauteurLambrequin);
      } else if (isDoublePente) {
        avanceeBase = parseFloat(dimensions.projection);
        avancee2Value = parseFloat(dimensions.projection2);
      } else {
        avanceeBase = parseFloat(dimensions.projection);
      }
      
      const { avanceeAjustee, details } = calculateAvanceeAjustee({
        avanceeBase,
        avancee2: avancee2Value,
        isLambrequinSeul,
        isDoublePente,
        fixationHaute: fixations.fixationHaute,
        fixationBasse: fixations.fixationBasse,
        diametreJoncHaute: diametres.joncHaute,
        diametreJoncBasse: diametres.joncBasse,
        diametreFourreauBasse: diametres.fourreauBasse,
        joncMedianPosition: fixations.joncMedianPosition,
        lambrequin,
        hauteurLambrequin: dimensions.hauteurLambrequin,
        fixationLambrequin: fixations.fixationLambrequin,
        diametreJoncLambrequin: diametres.joncLambrequin,
        optionsSupp,
        lestageDetails
      });
      
      setDetailCalculAvancee(details);
      
      const response = await axios.post(`${BACKEND_URL}/api/calculate/fabric/`, {
        largeur: parseFloat(dimensions.largeur),
        avancee: avanceeAjustee,
        hauteur_lambrequin: lambrequin !== 'sans' ? parseFloat(dimensions.hauteurLambrequin) : 0,
        product_id: String(productId),
        is_pergola: isPergola || isBriseVue,
        type_assemblage: typeAssemblage
      });
      setFabricCalculation(response.data);
    } catch (error) {
      console.error('Erreur calcul métrage:', error);
      setCalculationError('Erreur lors du calcul du métrage');
    }
  }, [
    productId, isToileAuMetre, isLambrequinSeul, isDoublePente, isPergola, isBriseVue,
    dimensions, fixations, diametres, lambrequin, optionsSupp, lestageDetails,
    BACKEND_URL, largeurLaize
  ]);

  useEffect(() => {
    calculateFabric();
  }, [calculateFabric]);

  // Notifier le parent des changements de configuration (useCallback avec useMemo pour la config)
  const configurationData = useMemo(() => {
    const sanglonsCost = isPergola && optionsSupp.sanglons ? sanglons.nombre * sanglons.unitPrice : 0;
    const totalPriceWithOptions = (fabricCalculation?.price_total || 0) + sanglonsCost;
    
    if (isToileAuMetre) {
      return {
        metrage: dimensions.metrage,
        largeurLaize,
        quantity,
        totalPrice: fabricCalculation?.price_total || 0,
        fabricCalculation: fabricCalculation
      };
    }
    
    return {
      largeur: dimensions.largeur,
      projection: dimensions.projection,
      projection2: isDoublePente ? dimensions.projection2 : undefined,
      fixationHaute: fixations.fixationHaute,
      fixationBasse: fixations.fixationBasse,
      diametreJoncHaute: fixations.fixationHaute === 'jonc' ? diametres.joncHaute : undefined,
      diametreJoncBasse: fixations.fixationBasse === 'jonc' ? diametres.joncBasse : undefined,
      diametreFourreauBasse: fixations.fixationBasse === 'fourreau' ? diametres.fourreauBasse : undefined,
      joncMedianPosition: fixations.joncMedianPosition,
      lambrequin,
      hauteurLambrequin: dimensions.hauteurLambrequin,
      fixationLambrequin: lambrequin !== 'sans' && lambrequin !== 'aucun' ? fixations.fixationLambrequin : undefined,
      diametreJoncLambrequin: (lambrequin !== 'sans' && lambrequin !== 'aucun' && fixations.fixationLambrequin === 'jonc') ? diametres.joncLambrequin : undefined,
      diametreTubeLambrequin: (lambrequin !== 'sans' && lambrequin !== 'aucun' && 
        (fixations.fixationLambrequin === 'fourreau-pince-interieur' || 
         fixations.fixationLambrequin === 'fourreau-pince-exterieur' || 
         fixations.fixationLambrequin === 'fourreau-rapporte')) ? diametres.fourreauBasse : undefined,
      nombreSanglons: sanglons.nombre,
      sanglonsCost,
      optionsSupp,
      renfortLyreDetails,
      typeAssemblage,
      lestageDetails,
      quantity,
      totalPrice: totalPriceWithOptions,
      fabricCalculation: fabricCalculation,
      detailCalculAvancee: detailCalculAvancee
    };
  }, [
    dimensions, fixations, diametres, lambrequin, sanglons, optionsSupp,
    renfortLyreDetails, typeAssemblage, lestageDetails, quantity,
    fabricCalculation, detailCalculAvancee, isDoublePente, isPergola,
    isToileAuMetre, largeurLaize
  ]);

  useEffect(() => {
    if (onConfigChange) {
      onConfigChange(configurationData);
    }
  }, [configurationData, onConfigChange]);

  // Ajuster automatiquement la fixation du lambrequin en fonction de la fixation basse
  useEffect(() => {
    if (lambrequin !== 'sans' && lambrequin !== 'aucun') {
      if (fixations.fixationBasse === 'fourreau' && fixations.fixationLambrequin === 'jonc') {
        setFixations(prev => ({ ...prev, fixationLambrequin: 'agrafe' }));
      }
      if (fixations.fixationBasse === 'jonc' && fixations.fixationLambrequin === 'agrafe') {
        setFixations(prev => ({ ...prev, fixationLambrequin: 'jonc' }));
      }
    }
  }, [fixations.fixationBasse, lambrequin, fixations.fixationLambrequin]);

  // Helpers pour mettre à jour les états regroupés
  const updateDimension = useCallback((key, value) => {
    setDimensions(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateFixation = useCallback((key, value) => {
    setFixations(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateDiametre = useCallback((key, value) => {
    setDiametres(prev => ({ ...prev, [key]: value }));
  }, []);

  return (
    <div className="space-y-6">
      {isToileAuMetre ? (
        <ToileAuMetreForm
          metrage={dimensions.metrage}
          setMetrage={(value) => updateDimension('metrage', value)}
          largeurLaize={largeurLaize}
          fabricCalculation={fabricCalculation}
        />
      ) : (
        <>
          <DimensionsSelector
            largeur={dimensions.largeur}
            setLargeur={(value) => updateDimension('largeur', value)}
            projection={dimensions.projection}
            setProjection={(value) => updateDimension('projection', value)}
            projection2={dimensions.projection2}
            setProjection2={(value) => updateDimension('projection2', value)}
            hauteurLambrequin={dimensions.hauteurLambrequin}
            setHauteurLambrequin={(value) => updateDimension('hauteurLambrequin', value)}
            isDoublePente={isDoublePente}
            isLambrequinSeul={isLambrequinSeul}
            isBriseVue={isBriseVue}
            fabricCalculation={fabricCalculation}
          />

          <FabricCalculationDisplay
            fabricCalculation={fabricCalculation}
            detailCalculAvancee={detailCalculAvancee}
            showDetailCalcul={showDetailCalcul}
            setShowDetailCalcul={setShowDetailCalcul}
            isPergola={isPergola}
            optionsSupp={optionsSupp}
            nombreSanglons={sanglons.nombre}
            sanglonUnitPrice={sanglons.unitPrice}
            calculationError={calculationError}
          />

          {isLambrequinSeul && (
            <LambrequinSelector
              lambrequin={lambrequin}
              setLambrequin={setLambrequin}
              lambrequinOptions={lambrequinOptions}
              isDoublePente={isDoublePente}
              isLambrequinSeul={isLambrequinSeul}
            />
          )}

          {!isLambrequinSeul && !isPergola && !isBriseVue && (
            <FixationsSelector
              fixationHaute={fixations.fixationHaute}
              setFixationHaute={(value) => updateFixation('fixationHaute', value)}
              fixationBasse={fixations.fixationBasse}
              setFixationBasse={(value) => updateFixation('fixationBasse', value)}
              diametreJoncHaute={diametres.joncHaute}
              setDiametreJoncHaute={(value) => updateDiametre('joncHaute', value)}
              diametreJoncBasse={diametres.joncBasse}
              setDiametreJoncBasse={(value) => updateDiametre('joncBasse', value)}
              diametreFourreauBasse={diametres.fourreauBasse}
              setDiametreFourreauBasse={(value) => updateDiametre('fourreauBasse', value)}
              joncMedianPosition={fixations.joncMedianPosition}
              setJoncMedianPosition={(value) => updateFixation('joncMedianPosition', value)}
              fixationsHaute={fixationsHaute}
              fixationsBasse={fixationsBasse}
              isStoreDroit={isStoreDroit}
            />
          )}

          {!isLambrequinSeul && !isPergola && !isBriseVue && (
            <LambrequinSelector
              lambrequin={lambrequin}
              setLambrequin={setLambrequin}
              lambrequinOptions={lambrequinOptions}
              isDoublePente={isDoublePente}
              isLambrequinSeul={isLambrequinSeul}
            />
          )}

          {(isLambrequinSeul || (lambrequin !== 'sans' && lambrequin !== 'aucun')) && !isPergola && !isBriseVue && (
            <FixationLambrequinSelector
              fixationBasse={fixations.fixationBasse}
              fixationLambrequin={fixations.fixationLambrequin}
              setFixationLambrequin={(value) => updateFixation('fixationLambrequin', value)}
              diametreJoncLambrequin={diametres.joncLambrequin}
              setDiametreJoncLambrequin={(value) => updateDiametre('joncLambrequin', value)}
              hauteurLambrequin={dimensions.hauteurLambrequin}
              setHauteurLambrequin={(value) => updateDimension('hauteurLambrequin', value)}
              fixationsBasse={fixationsBasse}
            />
          )}

          <OptionsSupplementaires
            optionsSupplementaires={optionsSupplementaires}
            optionsSupp={optionsSupp}
            handleOptionChange={handleOptionChange}
            nombreSanglons={sanglons.nombre}
            setNombreSanglons={(value) => setSanglons(prev => ({ ...prev, nombre: value }))}
            isPergola={isPergola}
            renfortLyreDetails={renfortLyreDetails}
            setRenfortLyreDetails={setRenfortLyreDetails}
            largeur={dimensions.largeur}
            typeAssemblage={typeAssemblage}
            setTypeAssemblage={setTypeAssemblage}
            lestageDetails={lestageDetails}
            setLestageDetails={setLestageDetails}
          />

          {/* Aperçu visuel de la configuration (peut être caché si affiché ailleurs) */}
          {!hidePreview && (
            <ConfigurationPreview
              largeur={dimensions.largeur}
              projection={dimensions.projection}
              projection2={dimensions.projection2}
              hauteurLambrequin={dimensions.hauteurLambrequin}
              lambrequin={lambrequin}
              isDoublePente={isDoublePente}
              isLambrequinSeul={isLambrequinSeul}
              isPergola={isPergola}
              isBriseVue={isBriseVue}
              optionsSupp={optionsSupp}
              renfortLyreDetails={renfortLyreDetails}
              lestageDetails={lestageDetails}
              nombreSanglons={sanglons.nombre}
              fabricCalculation={fabricCalculation}
              marquageText={marquageText}
              marquageCalculation={marquageCalculation}
              marquageColor={marquageColor}
              productImage={productImage}
              productRal={productRal}
            />
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(ConfigurationForm);
